// Constrained, allowlist-only markdown parser for untrusted LLM output
// (ADR-0050 IA chat answers). Supports exactly: paragraphs, soft line
// breaks, bullet/numbered lists, bold, italic, inline code, and entity
// tokens `[[T3]]`. Everything else — headings, blockquotes, tables,
// fenced code, images, markdown links, raw HTML — is intentionally
// unsupported: unrecognized syntax is left as literal text, never
// interpreted as markup.
//
// An entity token is not a link: it carries only a short handle, and only
// handles the server returned in `entities` (passed in as `handles`)
// become an `entity` node. The renderer resolves the handle to a kind +
// UUID and builds the href from a fixed route table — no text from the
// answer ever reaches an href.
//
// This module returns a plain data AST, never an HTML string. The real
// security boundary lives one layer up, in MarkdownMessage.tsx: it maps
// this AST straight to React elements/text children, and React escapes
// text content by default. There is no dangerouslySetInnerHTML anywhere
// in this pipeline, so even a bug in this parser can only misplace
// attacker text, never turn it into an executable DOM node.
//
// Threat model: `source` is LLM-generated and reachable by prompt
// injection via a finding's free-text field the model may echo back
// (see apps/ai-svc/src/prompts/chat.ts for the corresponding
// system-prompt-side mitigation). Every constant below bounds a
// specific pathological-input cost — total render time, DOM node count,
// recursion depth — none of them sanitize markup, because none of the
// output is ever markup until React renders the AST as elements.
//
// The inline tokenizer regex is provably linear: every quantifier is a
// single bounded `{1,300}` over a class that excludes its own
// delimiter, and no quantifier wraps a group, so there is no nested- or
// ambiguous-alternation shape that backtracks catastrophically.
// MAX_INLINE_DEPTH is defense-in-depth, not the load-bearing bound —
// recursion always operates on a strict substring (delimiters are
// consumed before recursing), so it terminates unconditionally even
// with the depth cap removed.

const MAX_INPUT_LENGTH = 20_000; // chars; truncates a runaway answer
const MAX_BLOCKS = 200; // paragraphs + lists, combined
const MAX_LIST_ITEMS = 300; // per list
const MAX_INLINE_DEPTH = 4; // **_ nested emphasis _** recursion ceiling
const MAX_INLINE_TOKENS = 4000; // whole-message budget, not per-block —
// the budget object is created once in parseMarkdown and threaded
// through every call, so this caps total DOM node count for a message
// built from many tiny spans, not just one block's worth.

export type MdInline =
  | { type: "text"; value: string }
  | { type: "strong"; children: MdInline[] }
  | { type: "em"; children: MdInline[] }
  | { type: "code"; value: string }
  | { type: "entity"; handle: string }
  | { type: "break" };

type InlineBudget = { tokens: number; handles: ReadonlySet<string> };

export type MdBlock =
  | { type: "paragraph"; children: MdInline[] }
  | { type: "list"; ordered: boolean; start?: number; items: MdInline[][] };

// Up to 3 leading spaces tolerated (a lightweight nod to indentation
// LLM output sometimes carries) but no true nested-list support — the
// renderer flattens everything at one level, which matches the
// allowlist's "simple lists" scope.
const BULLET_RE = /^ {0,3}[-*+]\s+(.*)$/;
const ORDERED_RE = /^ {0,3}(\d{1,9})\.\s+(.*)$/;

const TRUNCATED_MARKER: MdBlock = {
  type: "paragraph",
  children: [{ type: "text", value: "…" }],
};

export function parseMarkdown(source: string, handles: ReadonlySet<string> = new Set()): MdBlock[] {
  const overLength = source.length > MAX_INPUT_LENGTH;
  const truncated = overLength ? source.slice(0, MAX_INPUT_LENGTH) : source;
  const lines = truncated.split("\n");
  const budget: InlineBudget = { tokens: 0, handles };
  const blocks: MdBlock[] = [];
  let paraLines: string[] = [];
  let capped = overLength;

  const flushParagraph = () => {
    if (paraLines.length === 0) return;
    if (blocks.length < MAX_BLOCKS) {
      const children: MdInline[] = [];
      paraLines.forEach((line, idx) => {
        if (idx > 0) children.push({ type: "break" });
        children.push(...parseInline(line, 0, budget));
      });
      blocks.push({ type: "paragraph", children });
    } else {
      capped = true;
    }
    paraLines = [];
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === "") {
      flushParagraph();
      i++;
      continue;
    }

    const bulletMatch = BULLET_RE.exec(line);
    const orderedMatch = bulletMatch ? null : ORDERED_RE.exec(line);

    if (bulletMatch || orderedMatch) {
      // A run of consecutive list-matching lines becomes one list block —
      // this is the fix for the all-or-nothing bug: an intro line
      // immediately followed by bullets (no blank line between them, the
      // model's most natural output shape) now correctly splits into a
      // paragraph block plus a list block, instead of the whole block
      // degrading to literal text because not every line matched.
      flushParagraph();
      const ordered = orderedMatch !== null;
      const items: MdInline[][] = [];
      let start: number | undefined;
      if (ordered) {
        const n = Number(orderedMatch[1]);
        if (Number.isFinite(n) && n !== 1) start = n;
      }
      while (i < lines.length) {
        const m = ordered ? ORDERED_RE.exec(lines[i]!) : BULLET_RE.exec(lines[i]!);
        if (!m) break;
        if (items.length >= MAX_LIST_ITEMS) {
          capped = true;
          break;
        }
        items.push(parseInline(m[ordered ? 2 : 1]!, 0, budget));
        i++;
      }
      // Skip any remaining list-shaped lines past the cap without
      // re-parsing them as a stray paragraph.
      while (i < lines.length) {
        const m = ordered ? ORDERED_RE.exec(lines[i]!) : BULLET_RE.exec(lines[i]!);
        if (!m) break;
        i++;
      }
      if (blocks.length < MAX_BLOCKS) {
        blocks.push(
          start !== undefined
            ? { type: "list", ordered, start, items }
            : { type: "list", ordered, items }
        );
      } else {
        capped = true;
      }
      continue;
    }

    paraLines.push(line);
    i++;
  }
  flushParagraph();

  // Any cap tripping (length, block count, or list-item count) is made
  // visible rather than silently dropping content — a manager reading a
  // truncated list of affected stores should know it's incomplete.
  if (capped) blocks.push(TRUNCATED_MARKER);

  return blocks;
}

function parseInline(text: string, depth: number, budget: InlineBudget): MdInline[] {
  if (depth >= MAX_INLINE_DEPTH || budget.tokens >= MAX_INLINE_TOKENS) {
    return text ? [{ type: "text", value: text }] : [];
  }

  // A fresh RegExp instance per call — recursion re-enters this function
  // for the content inside a matched bold/italic span, and a single
  // shared `g`-flag regex object would have its `.lastIndex` clobbered
  // by the inner call before the outer loop resumes, corrupting the
  // outer scan (duplicated/garbled spans).
  const tokenRe =
    /\[\[([TRO]\d{1,4})\]\]|`([^`]{1,300})`|\*\*([^*]{1,300}?)\*\*|__([^_]{1,300}?)__|\*([^*]{1,300}?)\*|_([^_]{1,300}?)_/g;
  const nodes: MdInline[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = tokenRe.exec(text)) !== null) {
    if (budget.tokens >= MAX_INLINE_TOKENS) break;
    if (match.index > lastIndex) {
      nodes.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    const [whole, handle, code, boldStar, boldUnder, italicStar, italicUnder] = match;
    budget.tokens++;
    if (handle !== undefined) {
      // A handle the server did not vouch for stays literal text.
      nodes.push(
        budget.handles.has(handle) ? { type: "entity", handle } : { type: "text", value: whole }
      );
    } else if (code !== undefined) {
      nodes.push({ type: "code", value: code });
    } else if (boldStar !== undefined || boldUnder !== undefined) {
      nodes.push({
        type: "strong",
        children: parseInline((boldStar ?? boldUnder)!, depth + 1, budget),
      });
    } else {
      nodes.push({
        type: "em",
        children: parseInline((italicStar ?? italicUnder)!, depth + 1, budget),
      });
    }
    lastIndex = tokenRe.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push({ type: "text", value: text.slice(lastIndex) });
  }
  return nodes;
}
